import React, { useState } from 'react';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { TableChart as DatasetsIcon } from '@mui/icons-material';
import { Panel, Group as PanelGroup } from 'react-resizable-panels';
import EmptyStateMessage from '../../../../shared/components/EmptyStateMessage';
import InlineAlert from '../../../../shared/components/InlineAlert';
import { ResizeHandle } from '../../../../shared/components/ResizeHandle';
import SortableTable, { type ColumnDef } from '../../../../shared/components/SortableTable';
import TimeRangeSelect from '../../../../shared/components/TimeRangeSelect';
import ViewToolbar from '../../../../shared/components/ViewToolbar';
import DatasetRowsFilterBar from './DatasetRowsFilterBar';
import DatasetRowDetailPanel from './DatasetRowDetailPanel';
import { useDatasetRowsBrowser, type RowsView } from '../../hooks/useDatasetRowsBrowser';
import { DEFAULT_SORT, RANGE_PRESETS, UNFILTERABLE } from '../../utils/datasetRowsUtils';
import type { EventDatasetRecord, EventDatasetRows } from '../../../../types/backend';
import type { RowEventTarget } from '../../../../types/frontend';

interface DatasetRowsViewProps {
  customerId: string;
  dataset: EventDatasetRecord;
  /** Given when a row's event can be opened in the Event Explorer. */
  onOpenEvent?: (target: RowEventTarget) => void;
}

interface TableRow {
  index: number;
  values: Record<string, string | null>;
}

const rowsMatch = (n: number) => `${n.toLocaleString('en-US')} ${n === 1 ? 'row matches' : 'rows match'}`;

function spanNote(rows: EventDatasetRows): string | null {
  if (!rows.span_note || !rows.since) return null;
  const start = `Showing from ${rows.since.slice(0, 10)} (retention start)`;
  const earliest = rows.freshness.earliest_history;
  return earliest ? `${start}; history holds rows from ${earliest}.` : `${start}.`;
}

/** A dataset's rows, read a page at a time from the customer's account with server-side filters and sort (EM-UC-37). */
const DatasetRowsView: React.FC<DatasetRowsViewProps> = ({ customerId, dataset, onOpenEvent }) => {
  const browser = useDatasetRowsBrowser(customerId, dataset);
  const { rows, types } = browser;
  // The open row belongs to the rows it was opened from; new rows close it.
  const [selected, setSelected] = useState<{ rows: EventDatasetRows; index: number } | null>(null);

  const data: TableRow[] = (rows?.rows ?? []).map((values, index) => ({ index, values }));
  const shownColumns = rows?.columns.length ? rows.columns : browser.columns.map((c) => c.name);
  const tableColumns: ColumnDef<TableRow>[] = shownColumns.map((name) => ({
    key: name,
    label: name,
    sortable: !UNFILTERABLE.includes(types.get(name) ?? 'json'),
    render: (row: TableRow) => (
      <Typography noWrap title={row.values[name] ?? ''} sx={{ fontSize: 13, maxWidth: 280 }}>{row.values[name] ?? ''}</Typography>
    ),
  }));

  const selectedRow = selected && rows && selected.rows === rows && !browser.stale ? data[selected.index] : undefined;
  const noRowsYet = !!rows && rows.rows.length === 0 && !rows.freshness.newest_event;
  const note = rows ? spanNote(rows) : null;

  const pager = (
    <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      {browser.atPagingCap && (
        <Typography variant="body2" color="text.secondary">
          Paging stops here. Narrow the filters or the range to see more rows.
        </Typography>
      )}
      <Box sx={{ flex: 1 }} />
      <Button size="small" disabled={browser.page === 1 || browser.loading} onClick={browser.previous}>
        Previous
      </Button>
      <Typography variant="body2" color="text.secondary">Page {browser.page}</Typography>
      <Button size="small" disabled={!browser.hasNext || browser.loading} onClick={browser.next}>
        Next
      </Button>
    </Box>
  );

  let body: React.ReactNode;
  if (browser.error) {
    // A failed read shows its alert and the pager alone: it says nothing about which rows match, and the
    // operator can still page back from it.
    body = <><Box sx={{ flex: 1 }} />{pager}</>;
  } else if (noRowsYet) {
    body = (
      <EmptyStateMessage
        icon={DatasetsIcon}
        title="This dataset holds no rows yet"
        description={rows.freshness.backfilled_through
          ? `Backfilled through ${rows.freshness.backfilled_through}`
          : 'The backfill has not started'}
      />
    );
  } else {
    body = (
      <PanelGroup orientation="horizontal" style={{ height: '100%', overflow: 'hidden' }}>
        <Panel minSize="30%">
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* The previous read's rows stay dimmed, and take no clicks, until the new read returns. */}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', opacity: browser.stale ? 0.5 : 1 }}>
              <SortableTable
                columns={tableColumns}
                data={data}
                rowKey={(row) => String(row.index)}
                selectedKey={selectedRow ? String(selected?.index) : null}
                onRowClick={(row) => rows && !browser.stale && setSelected({ rows, index: row.index })}
                loading={browser.loading && !rows}
                emptyMessage="No rows match these filters"
                defaultSortKey={DEFAULT_SORT.key}
                defaultSortDirection={DEFAULT_SORT.direction}
                sort={browser.sort}
                onSortChange={browser.setSort}
              />
            </Box>
            {pager}
          </Box>
        </Panel>

        {selectedRow && (
          <>
            <ResizeHandle />
            <Panel defaultSize="35%" minSize="20%" maxSize="60%">
              <DatasetRowDetailPanel
                columns={shownColumns}
                types={types}
                values={selectedRow.values}
                onClose={() => setSelected(null)}
                onOpenEvent={onOpenEvent}
              />
            </Panel>
          </>
        )}
      </PanelGroup>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} data-testid="dataset-rows-view">
      <ViewToolbar count={rows ? `${rows.rows.length} rows shown` : ''} onRefresh={browser.refresh} loading={browser.loading}>
        {dataset.keep_current_state && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={browser.view}
            onChange={(_, next: RowsView | null) => next && browser.setView(next)}
          >
            <ToggleButton value="history" sx={{ textTransform: 'none' }}>History</ToggleButton>
            <ToggleButton value="current" sx={{ textTransform: 'none' }}>Current state</ToggleButton>
          </ToggleButtonGroup>
        )}
        {browser.view === 'history' && (
          <TimeRangeSelect
            presets={RANGE_PRESETS}
            timeRange={browser.range.preset}
            startTime={browser.draft.since}
            endTime={browser.draft.until}
            onSelectPreset={browser.selectPreset}
            onStartChange={browser.setDraftSince}
            onEndChange={browser.setDraftUntil}
            onApply={browser.applyCustom}
          />
        )}
      </ViewToolbar>

      <DatasetRowsFilterBar columns={browser.filterable} filters={browser.filters} onChange={browser.setFilters}>
        {browser.matched !== undefined && (
          <Typography variant="body2" color="text.secondary">{rowsMatch(browser.matched)}</Typography>
        )}
        <Button size="small" onClick={browser.count} disabled={browser.counting}>
          Count matching rows
        </Button>
      </DatasetRowsFilterBar>

      {browser.error && <InlineAlert severity="error" sx={{ mx: 2, mt: 1, flexShrink: 0 }}>{browser.error}</InlineAlert>}
      {browser.countError && (
        <InlineAlert severity="error" sx={{ mx: 2, mt: 1, flexShrink: 0 }}>{browser.countError}</InlineAlert>
      )}
      {/* The notes are the shown rows' own, so they dim with them while a new read is on its way. */}
      {rows?.behind && (
        <InlineAlert severity="warning" sx={{ mx: 2, mt: 1, flexShrink: 0, opacity: browser.stale ? 0.5 : 1 }}>
          These rows may be incomplete: the dataset is {rows.freshness.state}.
        </InlineAlert>
      )}
      {note && (
        <InlineAlert severity="info" sx={{ mx: 2, mt: 1, flexShrink: 0, opacity: browser.stale ? 0.5 : 1 }}>{note}</InlineAlert>
      )}

      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>{body}</Box>
    </Box>
  );
};

export default DatasetRowsView;
